/**
 * Engagement & Inactivity Sequence Templates
 * Based on best practices from Duolingo, Grammarly, Headspace
 *
 * Key insights from billion-dollar companies:
 * - Re-engage inactive users within 7-14 days (before they forget you)
 * - Lead with value, not guilt
 * - Personalize based on what they DID use
 * - Make return easy with 1-click actions
 * - Duolingo's approach: Friendly, gamified, low-pressure
 */

import { SequenceTemplate } from "./types";

export const INACTIVITY_7_DAY: SequenceTemplate = {
  id: "inactivity-7-day",
  name: "We Miss You (7-Day Inactive)",
  type: "inactivity",
  description: "Re-engage users who haven't logged in for 7 days",
  strategy: "Duolingo-style friendly re-engagement. Focus on what they were working on, not what they're missing. Make return frictionless.",
  icon: "HeartOutlined",
  color: "#ec4899",
  expectedOutcomes: [
    "25-40% re-engagement rate",
    "Identify at-risk users early",
    "Prevent long-term churn",
    "Gather inactivity insights"
  ],
  bestPractices: [
    "Reference their last activity or project",
    "Don't guilt-trip - be friendly and helpful",
    "Offer a quick win to come back to",
    "Include 1-click login link",
    "Ask if they need help (maybe they're stuck)"
  ],
  trigger: {
    eventType: "inactivity",
    delay: { value: 7, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "inactive",
      engagementLevel: "low"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Day 7 - Friendly Check-in",
      subject: "{{first_name}}, your AI copilots are waiting!",
      suggestedTemplateId: "re-engagement",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Friendly re-engagement without pressure",
      variables: [
        { key: "last_project", defaultValue: "{{last_copilot_used}}" },
        { key: "quick_action", defaultValue: "Check your generation stats" }
      ]
    },
    {
      name: "Day 10 - Helpful Tips",
      subject: "Quick tip to improve your AI copilot results",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Provide value + easy action to return"
    },
    {
      name: "Day 14 - What's New",
      subject: "New features you might have missed",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "FOMO + showcase improvements"
    }
  ],
  tags: ["inactivity", "re-engagement", "7-day", "win-back"],
  bestFor: ["Habit-forming products", "Daily-use tools", "Any SaaS"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const INACTIVITY_30_DAY: SequenceTemplate = {
  id: "inactivity-30-day",
  name: "Deep Re-engagement (30-Day Inactive)",
  type: "inactivity",
  description: "Last-effort sequence for long-inactive users",
  strategy: "For users who've been gone a month. Lead with significant updates, offer fresh start, consider downgrade options to keep them in ecosystem.",
  icon: "ReloadOutlined",
  color: "#6366f1",
  expectedOutcomes: [
    "10-20% re-engagement rate",
    "Identify truly churned users",
    "Collect feedback on why they left",
    "Clean up inactive accounts"
  ],
  bestPractices: [
    "Acknowledge the gap - don't pretend it didn't happen",
    "Offer a 'fresh start' or account reset option",
    "Include a survey to understand why they left",
    "Consider offering a downgrade vs. full cancel",
    "This is your last chance - make it count"
  ],
  trigger: {
    eventType: "inactivity",
    delay: { value: 30, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "inactive",
      engagementLevel: "low"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Day 30 - We've Changed",
      subject: "{{first_name}}, a lot has changed since you left",
      suggestedTemplateId: "re-engagement",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Highlight major improvements"
    },
    {
      name: "Day 35 - Fresh Start Offer",
      subject: "Want a fresh start? We'll set it up for you",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Offer hands-on help to restart"
    },
    {
      name: "Day 40 - Feedback Request",
      subject: "1 minute survey: Help us improve",
      suggestedTemplateId: "product-survey",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Collect feedback + show you care"
    },
    {
      name: "Day 45 - Final Offer",
      subject: "Before you go: Special offer inside",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Last chance with compelling offer"
    }
  ],
  tags: ["inactivity", "re-engagement", "30-day", "deep-recovery"],
  bestFor: ["Subscription products", "Products with setup investment"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const FEATURE_ADOPTION: SequenceTemplate = {
  id: "feature-adoption",
  name: "Feature Adoption Campaign",
  type: "engagement",
  description: "Drive adoption of underused features",
  strategy: "Educate users about features they're not using. Segment by feature usage and send targeted education. Can increase stickiness by 40%.",
  icon: "BulbOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "30-50% feature adoption increase",
    "Higher product stickiness",
    "Increased perceived value",
    "Better upgrade potential"
  ],
  bestPractices: [
    "Segment users by feature usage",
    "Show use cases, not just features",
    "Include video tutorials",
    "Highlight benefits, not just how-to",
    "Time emails when users are most active"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 14, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "medium"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Feature 1 - Analytics",
      subject: "Are you using analytics? Here's what you're missing",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Introduce analytics features",
      variables: [
        { key: "feature_name", defaultValue: "Analytics Dashboard" },
        { key: "feature_benefit", defaultValue: "Track your AI copilot usage and performance" }
      ]
    },
    {
      name: "Feature 2 - Integrations",
      subject: "Connect {{product_name}} to your favorite tools",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Drive integration adoption"
    },
    {
      name: "Feature 3 - AI Models",
      subject: "Explore different AI models for better results",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Showcase different AI model capabilities"
    },
    {
      name: "Feature 4 - Copilots",
      subject: "50+ AI copilots you haven't tried yet",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Highlight AI copilot library"
    }
  ],
  tags: ["engagement", "feature-adoption", "education", "stickiness"],
  bestFor: ["Feature-rich products", "Complex platforms", "Products with low adoption"],
  difficulty: "intermediate",
  setupTime: "20 minutes"
};

export const NPS_FEEDBACK: SequenceTemplate = {
  id: "nps-feedback",
  name: "NPS & Feedback Collection",
  type: "engagement",
  description: "Collect NPS scores and actionable feedback",
  strategy: "Send NPS survey at optimal moments (after success, 30 days in). Follow up based on score: promoters get referral ask, detractors get support.",
  icon: "StarOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "30-40% NPS response rate",
    "Identify promoters for referrals",
    "Catch detractors before churn",
    "Actionable product feedback"
  ],
  bestPractices: [
    "Send at moments of success (after first achievement)",
    "Keep survey SHORT (1 question to start)",
    "Follow up differently based on score",
    "Close the loop - tell them how you used feedback",
    "Offer incentive for detailed feedback"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 30, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "NPS Survey",
      subject: "Quick question: How likely are you to recommend us?",
      suggestedTemplateId: "product-survey",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Collect NPS score"
    },
    {
      name: "Follow-up - Promoters",
      subject: "Thanks! Would you share the love?",
      suggestedTemplateId: "product-upsell",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Ask promoters for referral/review"
    },
    {
      name: "Follow-up - Detractors",
      subject: "We want to make it right - can we talk?",
      suggestedTemplateId: "product-feedback",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Reach out to unhappy users"
    }
  ],
  tags: ["nps", "feedback", "survey", "engagement", "referral"],
  bestFor: ["Any product", "Growth-stage companies", "Customer-centric businesses"],
  difficulty: "beginner",
  setupTime: "15 minutes"
};

export const ENGAGEMENT_TEMPLATES = [
  INACTIVITY_7_DAY,
  INACTIVITY_30_DAY,
  FEATURE_ADOPTION,
  NPS_FEEDBACK
];
